import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../Common/Header.jsx';
import Join from './Join.jsx';
import { HeadphonesAffordance, Card, Badge } from '../Common/DesignComponents.jsx';
import PhaseStatus from '../Common/PhaseStatus.jsx';
import { SoundOffPlayer } from '../modes/SoundOff/SoundOffPlayer.jsx';
import DriftPlayer from '../modes/Drift/DriftPlayer.jsx';
import { NameThatNoisePlayer } from '../modes/NameThatNoise/NameThatNoisePlayer.jsx';
import { FoleyPlayer } from '../modes/Foley/FoleyPlayer.jsx';
import useAudioEngine from '../../audio/useAudioEngine.js';
import { APP_VERSION } from '../../utils/version.js';
import io from '../../utils/socketWrapper.js';
import '../../styles/theme.css';

// A stable id per (room, name) so a refresh or a dropped socket reconnects into
// the same seat rather than creating a duplicate player.
function stablePlayerId(roomCode, playerName) {
  const key = `sounddrift:pid:${roomCode}:${playerName}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = 'p_' + Math.random().toString(36).substring(2, 11);
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return 'p_' + Math.random().toString(36).substring(2, 11);
  }
}

export function PlayerShell({ children, mockRoom = null, mockJoined = false }) {
  const [searchParams] = useSearchParams();
  const roomCodeParam = searchParams.get('roomCode') || '';
  const playerNameParam = searchParams.get('playerName') || searchParams.get('debug_name') || '';

  const [roomCode, setRoomCode] = useState(roomCodeParam);
  const [playerName, setPlayerName] = useState(playerNameParam);
  const [playerAvatar, setPlayerAvatar] = useState('🎵');

  // `joined` means "we have actually opened a socket and sent join_room".
  //
  // It used to be initialised from the presence of the URL params — but the
  // auto-join effect below is guarded on !joined, so having the params set the
  // flag meant the join never ran. The phone rendered as joined, showing the
  // right room code and name, while no socket existed and the host never saw
  // the player. Only mock props may pre-set this.
  const [joined, setJoined] = useState(mockJoined || Boolean(mockRoom));
  const [playerId, setPlayerId] = useState('');
  const [room, setRoom] = useState(mockRoom);
  const [isConnected, setIsConnected] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [progress, setProgress] = useState(null);

  // Phones audition at reduced gain; the host TV is the only full-volume
  // speaker (REQUIREMENTS §2).
  const audio = useAudioEngine('player');

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data && (e.data.type === 'UNLOCK_AUDIO' || e.data.type === 'ENABLE_SOUND')) {
        audio.unlock();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [audio]);

  const socketRef = useRef(null);

  const connectAndJoin = useCallback(({ roomCode: code, playerName: name, avatar }) => {
    if (!code || !name || socketRef.current) return;

    const id = stablePlayerId(code, name);
    setRoomCode(code);
    setPlayerName(name);
    if (avatar) setPlayerAvatar(avatar);
    setPlayerId(id);
    setJoined(true);

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('sounddrift_roomCode', code);
    }

    const s = io(`${window.location.origin}?roomCode=${code}`, { query: { roomCode: code }, reconnection: true });
    socketRef.current = s;

    s.on('connect', () => {
      setIsConnected(true);
      s.emit('join_room', { roomCode: code, playerId: id, playerName: name, avatar });
    });

    s.on('joined_room', (data) => {
      if (data?.playerId) setPlayerId(data.playerId);
      if (data?.room) setRoom(data.room);
    });

    s.on('room_update', (data) => {
      if (data?.room) setRoom(data.room);
      if (data?.progress) setProgress(data.progress);
    });
    s.on('game_started', (data) => { if (data?.room) setRoom(data.room); });
    s.on('phase_change', (data) => {
      if (data?.room) setRoom(data.room);
      if (data?.progress) setProgress(data.progress);
    });
    s.on('action_error', (data) => setActionError(data?.error || 'Action rejected'));
    s.on('action_acknowledged', () => setActionError(null));
    s.on('disconnect', () => setIsConnected(false));
  }, []);

  useEffect(() => {
    if (mockRoom) return;
    if (roomCodeParam && playerNameParam) {
      connectAndJoin({ roomCode: roomCodeParam, playerName: playerNameParam, avatar: '🎵' });
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [roomCodeParam, playerNameParam, mockRoom, connectAndJoin]);

  const sendAction = useCallback((payload) => {
    if (!socketRef.current) return;
    socketRef.current.emit('submit_action', { roomCode, ...payload });
  }, [roomCode]);

  // The server writes `currentPhase`; reading `room.phase` here meant the phone
  // believed it was always in the lobby, exactly as the host shell did.
  const phase = room?.currentPhase || 'lobby';
  const currentMode = room?.currentMode || 'soundoff';
  const modeState = room?.modeState || {};

  // Derived from the server's waiting list rather than local state, so the
  // phone and the engine can never disagree about whether this player is done.
  const hasActedThisPhase = Boolean(
    progress && playerId && !progress.waitingOn?.some((p) => p.id === playerId)
  );

  const isSelectionPhase = ['authoring', 'answering', 'bluffing', 'select', 'selection'].includes(phase);
  const isRevealOrVotePhase = ['duel', 'voting', 'reveal', 'vote', 'scoring', 'results'].includes(phase);

  const renderActiveMode = () => {
    if (children) return children;
    if (phase === 'lobby') return null;

    switch (currentMode) {
      case 'soundoff':
        return <SoundOffPlayer room={room} playerId={playerId} audioEngine={audio} onSendAction={sendAction} />;
      case 'drift':
        return (
          <DriftPlayer
            room={room}
            phase={phase}
            modeState={modeState}
            playerId={playerId}
            hasSubmitted={hasActedThisPhase}
            onSubmitStep={(stepData) => {
              sendAction({ actionType: 'submit_step', ...stepData });
            }}
            onSubmitVote={(voteData) => {
              sendAction({ actionType: 'submit_vote', ...voteData });
            }}
            onSubmitAction={sendAction}
            audioEngine={audio}
          />
        );
      case 'namethatnoise':
        return (
          <NameThatNoisePlayer
            phase={phase}
            modeState={modeState}
            playerId={playerId}
            hasSubmitted={hasActedThisPhase}
            onSubmitBluff={(bluffData) => {
              const bluffVal = typeof bluffData === 'string' ? bluffData : (bluffData?.bluff || bluffData?.text || '');
              sendAction({ actionType: 'submit_bluff', bluff: bluffVal, text: bluffVal });
            }}
            onSubmitVote={(voteData) => {
              const voteVal = typeof voteData === 'string' ? voteData : (voteData?.voteOptionId || voteData?.vote || voteData?.optionId);
              sendAction({ actionType: 'submit_vote', vote: voteVal, voteOptionId: voteVal });
            }}
          />
        );
      case 'foley':
        return (
          <FoleyPlayer
            room={room}
            phase={phase}
            modeState={modeState}
            playerId={playerId}
            hasSubmitted={hasActedThisPhase}
            audioEngine={audio}
            socket={socketRef.current}
            onSendAction={sendAction}
          />
        );
      default:
        return null;
    }
  };

  const activeModeView = joined ? renderActiveMode() : null;

  return (
    <div
      className="player-phone-shell"
      data-testid="player-shell"
      style={{
        width: '100%',
        maxWidth: '420px',
        minHeight: '100vh',
        margin: '0 auto',
        background: '#08090B',
        color: '#EDEFF2',
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      {joined && (
        <Header
          isHost={false}
          roomCode={roomCode}
          playerName={playerName}
          playerAvatar={playerAvatar}
          isConnected={isConnected}
        />
      )}

      <HeadphonesAffordance visible={joined && (isSelectionPhase || phase === 'lobby')} />

      {!joined ? (
        <Join
          initialRoomCode={roomCodeParam}
          initialPlayerName={playerNameParam}
          onJoin={connectAndJoin}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <PhaseStatus
            phase={phase}
            progress={progress}
            phaseEndTime={room?.phaseEndTime}
            compact
          />

          {actionError && (
            <div
              data-testid="player-action-error"
              role="alert"
              style={{
                background: 'rgba(244, 63, 94, 0.15)',
                border: '1px solid #E4738A',
                color: '#E4738A',
                padding: '8px 14px',
                borderRadius: '10px',
                fontSize: '0.85rem',
                fontWeight: 700,
                textAlign: 'center',
                margin: '8px 0',
              }}
            >
              {actionError}
            </div>
          )}

          {isRevealOrVotePhase && (
            <div
              data-testid="player-muted-indicator"
              style={{
                background: 'rgba(244, 63, 94, 0.15)',
                border: '1px solid #E4738A',
                color: '#E4738A',
                padding: '8px 14px',
                borderRadius: '10px',
                fontSize: '0.85rem',
                fontWeight: 700,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                margin: '8px 0',
              }}
            >
              <span>🔇</span> Device Muted for Host TV Playback
            </div>
          )}

          <div className="sd-scroll" style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {activeModeView || (
              <Card variant="default" style={{ textAlign: 'center', padding: '32px 16px', marginTop: 'auto', marginBottom: 'auto' }}>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📺</div>
                <h2 style={{ fontSize: '1.6rem', color: '#EDEFF2', margin: '0 0 8px 0', fontWeight: 800 }}>
                  Look at the TV Screen!
                </h2>
                <p style={{ color: '#79838D', fontSize: '1rem', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                  You are in room <strong style={{ color: '#3ACFCF' }}>{roomCode}</strong> as{' '}
                  <strong style={{ color: '#EDEFF2' }}>{playerName}</strong>.
                </p>
                <Badge variant="cyan" size="lg">
                  Phase: {phase.toUpperCase()}
                </Badge>
              </Card>
            )}
          </div>

          <div
            data-testid="player-bottom-bar"
            style={{
              marginTop: 'auto',
              padding: '12px 16px',
              borderTop: '1px solid #1A1F25',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.8rem',
              color: '#6E7982',
              fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
              minHeight: '44px',
            }}
          >
            <span>SoundDrift {APP_VERSION}</span>
            <span>Room: {roomCode || '---'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayerShell;
